import {
  Fragment, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Avatar } from '../../components/shared/Avatar';
import {
  ChevronLeft, ChevronRight, Check, Clock, Star, AlertTriangle, X,
} from '../../icons';
import {
  listResources, listResourceSlots, createReservation,
  type ReservationDto, type ReservationResourceDto,
} from '../../api/reservation';
import { tRsv, tRsvDuration, getRsvLang, subscribeRsvLang } from '../../lib/reservation-i18n';
import { isReservationInThePast } from '../../lib/reservation-time';
import {
  reservationsOfDay, peakUsed, dayKey, fmtDate, fmtTime, fmtRange,
} from './reservation-calendar';
import {
  HOUR_PX, buildHourWindow, buildMonthWeeks, buildWeekDays, dayFreeRatio, dayRemainingCount,
  inMonth, layoutDayEvents, layoutSpanBars, liveStatus,
  freeMinutesOfDay, minuteToY, yToMinute, snapToSlot, startOfDay, startOfWeek,
  type HourWindow, type LiveStatus, type SpanBar, type WeekDay,
} from './reservation-week';
import { ReservationAside } from './ReservationAside';
// 화면 머리 — 근태 · 메일과 같은 정본 A3 (2026-09-08).
import { PageHeader } from '../../components/shared/PageHeader';
import './ReservationPage.css';

/**
 * 예약하기 — 시안 `예약 화면 재설계` 반영 (2026-08-20).
 *
 * 바뀐 것(시안 진단 그대로):
 *   · 자원 목록·필터가 ② 좌측 상세로 간다(포털) — 컨텐츠는 달력에 자리를 돌려준다.
 *   · 기본 뷰가 **주간 타임라인**이다. 남의 예약을 이름과 함께 그리고, 빈 곳을 끌어 시간을 고른다.
 *     월 뷰는 토글로 남는다(일 단위 자원은 월 뷰만 쓴다 — 날짜 범위 선택이라 타임라인이 의미 없다).
 *   · 신청 영역이 컨텐츠 오른쪽에 고정된다 — 선택하면 채워지고 버튼이 스크롤 밖으로 안 나간다.
 *   · 자원 카드가 "지금 상태"(지금 가능 / 사용 중 · N시 해제)를 먼저 답한다.
 *
 * 서버 변경 없음 — 이미 내려오던 슬롯 응답(신청자·시간)을 그대로 쓴다. 잔여 계산은 안내용이고
 * 최종 판정은 서버가 다시 한다(신청 순간에 다른 사람이 채웠을 수 있다).
 */

/** 요일 머리 — 월요일 시작(시안). 월 뷰(일요일 시작)와 축이 다르니 배열을 따로 둔다. */
const WEEK_DOW = ['월', '화', '수', '목', '금', '토', '일'];

/** 막대 라벨용 짧은 날짜 범위 — `8/22–8/24`. fmtRange 는 연도까지 써서 칸에 안 들어간다. */
function fmtDayShort(start: Date, end: Date): string {
  const last = new Date(end.getTime() - 1);   // 저장은 반납 다음날 자정(배타)
  const p = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
  return dayKey(start) === dayKey(last) ? p(start) : `${p(start)}–${p(last)}`;
}

/** 즐겨찾기는 기기별(localStorage) — 계정 동기화는 서버가 있어야 한다(시안 구현범위 표). */
const FAV_KEY = 'rsv.favorites';

/** 지금 상태 조회는 자원 수만큼 요청이 나간다 — 상한과 동시성을 둬 버스트를 막는다. */
const LIVE_MAX_RESOURCES = 24;
const LIVE_CONCURRENCY = 4;

type ViewMode = 'week' | 'month';
type ResourceFilter = 'all' | 'now' | 'instant' | `cat:${string}`;

function readFavorites(): string[] {
  try {
    const raw = window.localStorage.getItem(FAV_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeFavorites(ids: string[]): void {
  try { window.localStorage.setItem(FAV_KEY, JSON.stringify(ids)); } catch { /* 사생활 모드 등 — 무시 */ }
}

/** 동시 요청 수를 묶어 순서대로 흘린다(rate-limit 버스트 방지). */
async function mapLimit<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    for (;;) {
      const i = cursor;
      cursor += 1;
      if (i >= items.length) return;
      out[i] = await fn(items[i]!);
    }
  });
  await Promise.all(workers);
  return out;
}

interface DragState {
  dayKey: string;
  anchorMinute: number;
  mode: 'new' | 'start' | 'end';
}

export function ReservationBookPage() {
  useSyncExternalStore(subscribeRsvLang, getRsvLang, getRsvLang);
  // "같은 조건으로 예약"(나의 예약)에서 넘어올 때 어떤 자원인지 — 없으면 첫 자원.
  const [searchParams] = useSearchParams();
  const wantedResourceId = searchParams.get('resource');

  const [resources, setResources] = useState<ReservationResourceDto[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ResourceFilter>('all');
  const [favorites, setFavorites] = useState<string[]>(() => readFavorites());
  const [listErr, setListErr] = useState<string | null>(null);
  const [live, setLive] = useState<Record<string, LiveStatus>>({});

  const [view, setView] = useState<ViewMode>('week');
  const [cursor, setCursor] = useState(() => new Date());
  const [reservations, setReservations] = useState<ReservationDto[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // 선택 — 시간 단위는 주간 끌기(weekSel), 일 단위는 날짜 범위(rangeStart/End).
  // 둘 다 하나의 pending 으로 모인다.
  const [weekSel, setWeekSel] = useState<{ start: Date; end: Date } | null>(null);
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);

  const [purpose, setPurpose] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 지금 시각 — 1분마다 갱신(현재선·"지금 가능" 판정이 멈춰 있지 않게).
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const dragRef = useRef<DragState | null>(null);
  // "지금 바로" 칩으로 자원을 바꿀 때, 자원 변경이 선택을 지우지 않게 넘겨 주는 자리.
  const carryRef = useRef<{ start: Date; end: Date } | null>(null);

  const selected = useMemo(
    () => resources.find((r) => r.id === selectedId) ?? null,
    [resources, selectedId],
  );

  // 주·월 둘 다 두 단위에 모두 있다 — 시간 단위는 시각 격자, 일 단위는 날짜 막대로 그린다.
  const effectiveView: ViewMode = view;

  useEffect(() => {
    let cancelled = false;
    void listResources()
      .then((rows) => {
        if (cancelled) return;
        setResources(rows);
        const wanted = wantedResourceId && rows.some((r) => r.id === wantedResourceId)
          ? wantedResourceId
          : null;
        setSelectedId((cur) => wanted ?? cur ?? rows[0]?.id ?? null);
      })
      .catch(() => { if (!cancelled) setListErr(tRsv('rsv.resources.err')); });
    return () => { cancelled = true; };
  }, [wantedResourceId]);

  // ── 자원 카드의 "지금 상태" — 오늘 구간을 자원마다 한 번씩 훑는다(시안 구현범위: 프론트만).
  useEffect(() => {
    if (resources.length === 0) return;
    let cancelled = false;
    const from = startOfDay(new Date());
    const to = new Date(from.getTime() + 24 * 60 * 60 * 1000);
    const targets = resources.slice(0, LIVE_MAX_RESOURCES);
    void mapLimit(targets, LIVE_CONCURRENCY, async (r) => {
      try {
        const res = await listResourceSlots(r.id, from, to);
        return { id: r.id, status: liveStatus(r, res.reservations, new Date()) };
      } catch {
        return null;
      }
    }).then((rows) => {
      if (cancelled) return;
      const next: Record<string, LiveStatus> = {};
      for (const row of rows) if (row) next[row.id] = row.status;
      setLive(next);
    });
    return () => { cancelled = true; };
    // 자원 목록이 바뀔 때만 — 1분 틱마다 N 요청을 다시 쏘지 않는다.
  }, [resources]);

  // ── 보이는 기간의 예약 현황.
  const reloadSlots = useCallback(async (resourceId: string, mode: ViewMode, anchor: Date) => {
    let from: Date;
    let to: Date;
    if (mode === 'week') {
      from = startOfWeek(anchor);
      to = new Date(from.getTime() + 7 * 24 * 60 * 60 * 1000);
    } else {
      from = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      from.setDate(from.getDate() - 7);
      to = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
      to.setDate(to.getDate() + 7);
    }
    setLoadingSlots(true);
    try {
      const res = await listResourceSlots(resourceId, from, to);
      setReservations(res.reservations);
      setError(null);
    } catch {
      setReservations([]);
      setError(tRsv('rsv.resources.err'));
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    void reloadSlots(selectedId, effectiveView, cursor);
  }, [selectedId, effectiveView, cursor, reloadSlots]);

  // 자원을 바꾸면 선택을 비운다(단위가 달라 의미가 안 맞는다). 퀵칩이 넘겨준 선택만 살린다.
  useEffect(() => {
    const carried = carryRef.current;
    carryRef.current = null;
    setRangeStart(null); setRangeEnd(null);
    setWeekSel(carried);
    setNotice(null); setError(null); setQuantity(1); setPurpose('');
  }, [selectedId]);

  // ── 자원 목록(② 좌측 상세) ────────────────────────────────────────
  const categories = useMemo(() => {
    const seen: string[] = [];
    for (const r of resources) if (!seen.includes(r.category)) seen.push(r.category);
    return seen;
  }, [resources]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return resources.filter((r) => {
      if (q) {
        const hit = r.name.toLowerCase().includes(q)
          || r.category.toLowerCase().includes(q)
          || (r.location ?? '').toLowerCase().includes(q)
          || (r.owner?.name ?? '').toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (filter === 'now') return live[r.id]?.kind === 'free';
      if (filter === 'instant') return !r.requiresApproval;
      if (filter.startsWith('cat:')) return r.category === filter.slice(4);
      return true;
    });
  }, [resources, query, filter, live]);

  /** 즐겨찾기를 맨 위 그룹으로 올리고 나머지는 분류별로 묶는다(시안). */
  const groups = useMemo(() => {
    const favRows = filtered.filter((r) => favorites.includes(r.id));
    const rest = filtered.filter((r) => !favorites.includes(r.id));
    const byCat = new Map<string, ReservationResourceDto[]>();
    for (const r of rest) {
      const arr = byCat.get(r.category) ?? [];
      arr.push(r);
      byCat.set(r.category, arr);
    }
    const out: Array<{ key: string; label: string; rows: ReservationResourceDto[] }> = [];
    if (favRows.length > 0) out.push({ key: '__fav', label: tRsv('rsv.fav.group'), rows: favRows });
    for (const [cat, rows] of byCat) out.push({ key: cat, label: cat, rows });
    return out;
  }, [filtered, favorites]);

  function toggleFavorite(id: string) {
    setFavorites((cur) => {
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
      writeFavorites(next);
      return next;
    });
  }

  // ── 주간 타임라인 ────────────────────────────────────────────────
  const win: HourWindow = useMemo(
    () => buildHourWindow(selected ?? { openMinute: 9 * 60, closeMinute: 18 * 60 }),
    [selected],
  );
  const weekDays: WeekDay[] = useMemo(
    () => buildWeekDays(cursor, now, selected?.maxDaysAhead ?? 0),
    [cursor, now, selected],
  );

  const weekColumns = useMemo(() => {
    if (!selected || selected.bookingUnit !== 'time') return [];
    return weekDays.map((d) => ({
      day: d,
      events: layoutDayEvents(reservations, d.date, win, now),
      freeMinutes: d.unavailable ? 0 : freeMinutesOfDay(selected, d.date, reservations, now),
    }));
  }, [selected, weekDays, reservations, win, now]);

  /** 월 그리드 6주 — 주간 뷰와 같은 월요일 시작 축. */
  const monthWeeks = useMemo(
    () => buildMonthWeeks(cursor, now, selected?.maxDaysAhead ?? 0),
    [cursor, now, selected],
  );

  /** 지금 바로 쓸 수 있는 자원 — 시안의 상단 퀵 칩. */
  const quickFree = useMemo(
    () => resources
      .filter((r) => live[r.id]?.kind === 'free' && r.bookingUnit === 'time')
      .slice(0, 4),
    [resources, live],
  );

  const pending = useMemo(() => {
    if (!selected) return null;
    if (selected.bookingUnit === 'day') {
      if (!rangeStart) return null;
      const last = rangeEnd ?? rangeStart;
      return {
        start: new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate()),
        end: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1),
      };
    }
    return weekSel;
  }, [selected, weekSel, rangeStart, rangeEnd]);

  /** 선택 구간의 잔여 — 0 이면 신청 버튼을 막고 이유를 보여준다(서버가 어차피 거절한다). */
  const pendingRemaining = useMemo(() => {
    if (!selected || !pending) return null;
    const used = peakUsed(reservationsOfDay(reservations, pending.start), pending.start, pending.end);
    return Math.max(0, selected.capacity - used);
  }, [selected, pending, reservations]);

  const pendingMinutes = pending
    ? Math.round((pending.end.getTime() - pending.start.getTime()) / 60000)
    : 0;

  function clearSelection() {
    setWeekSel(null); setRangeStart(null); setRangeEnd(null);
  }

  // Esc 로 선택 취소 — 시안의 안내 문구와 동작을 맞춘다.
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') clearSelection(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending]);

  // ── 끌어서 시간 고르기 ───────────────────────────────────────────
  function minuteFromEvent(e: ReactPointerEvent<HTMLElement>): number {
    const box = e.currentTarget.getBoundingClientRect();
    return yToMinute(e.clientY - box.top, win);
  }

  function commitSelection(day: Date, aMin: number, bMin: number) {
    if (!selected) return;
    const lo = snapToSlot(Math.min(aMin, bMin), selected, 'floor');
    const hiRaw = snapToSlot(Math.max(aMin, bMin), selected, 'ceil');
    const hi = Math.max(lo + selected.slotMinutes, hiRaw);
    if (hi > selected.closeMinute) return;
    const start = startOfDay(day);
    start.setMinutes(lo);
    const end = startOfDay(day);
    end.setMinutes(hi);
    // 판정은 lib/reservation-time 한 곳 — 서버 validateRequestedRange 와 같은 잣대다(IN_THE_PAST).
    if (isReservationInThePast(end, now)) return;
    setWeekSel({ start, end });
    setNotice(null); setError(null);
  }

  function onColumnPointerDown(e: ReactPointerEvent<HTMLDivElement>, day: WeekDay) {
    if (!selected || selected.bookingUnit !== 'time' || day.unavailable) return;
    if (e.button !== 0) return;
    const minute = minuteFromEvent(e);
    dragRef.current = { dayKey: dayKey(day.date), anchorMinute: minute, mode: 'new' };
    e.currentTarget.setPointerCapture(e.pointerId);
    commitSelection(day.date, minute, minute);
  }

  function onColumnPointerMove(e: ReactPointerEvent<HTMLDivElement>, day: WeekDay) {
    const drag = dragRef.current;
    if (!drag || drag.dayKey !== dayKey(day.date)) return;
    const minute = minuteFromEvent(e);
    if (drag.mode === 'new') {
      commitSelection(day.date, drag.anchorMinute, minute);
      return;
    }
    // 손잡이 끌기 — 반대쪽 끝을 고정한 채 이 끝만 옮긴다.
    if (!weekSel) return;
    const fixed = drag.mode === 'start'
      ? weekSel.end.getHours() * 60 + weekSel.end.getMinutes()
      : weekSel.start.getHours() * 60 + weekSel.start.getMinutes();
    commitSelection(day.date, fixed, minute);
  }

  function endDrag(e: ReactPointerEvent<HTMLDivElement>) {
    if (!dragRef.current) return;
    dragRef.current = null;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }

  function onGripPointerDown(e: ReactPointerEvent<HTMLSpanElement>, day: WeekDay, edge: 'start' | 'end') {
    e.stopPropagation();
    if (!selected) return;
    dragRef.current = { dayKey: dayKey(day.date), anchorMinute: 0, mode: edge };
    // 캡처는 열(column)이 받아야 이동이 계속 들어온다.
    const col = (e.currentTarget.closest('.rsv-col') as HTMLElement | null);
    col?.setPointerCapture?.(e.pointerId);
  }

  // ── 일 단위: 날짜 칸을 끌어 대여 기간 고르기 ─────────────────────
  //   한 줄(주) 안에서는 끌기, 주를 넘길 때는 "시작일 클릭 → 반납일 클릭" 으로 잇는다.
  const dayDragRef = useRef<{ anchor: Date; moved: boolean } | null>(null);

  /** 가로 위치 → 그 줄의 몇 번째 날인가. */
  function dayFromRow(e: ReactPointerEvent<HTMLDivElement>, days: ReadonlyArray<WeekDay>): WeekDay | null {
    const box = e.currentTarget.getBoundingClientRect();
    if (box.width <= 0 || days.length === 0) return null;
    const i = Math.floor(((e.clientX - box.left) / box.width) * days.length);
    return days[Math.min(days.length - 1, Math.max(0, i))] ?? null;
  }

  function commitRange(a: Date, b: Date) {
    const lo = a.getTime() <= b.getTime() ? a : b;
    const hi = a.getTime() <= b.getTime() ? b : a;
    setRangeStart(startOfDay(lo));
    setRangeEnd(startOfDay(hi));
    setNotice(null); setError(null);
  }

  function onRowPointerDown(e: ReactPointerEvent<HTMLDivElement>, days: ReadonlyArray<WeekDay>) {
    if (!selected || selected.bookingUnit !== 'day' || e.button !== 0) return;
    const d = dayFromRow(e, days);
    if (!d || d.unavailable) return;
    dayDragRef.current = { anchor: d.date, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
    // 시작일만 찍혀 있고 그 뒤 날짜를 누르면 반납일로 — 주를 넘겨 고를 수 있게.
    if (rangeStart && !rangeEnd && d.date.getTime() > rangeStart.getTime()) {
      commitRange(rangeStart, d.date);
    } else {
      setRangeStart(startOfDay(d.date));
      setRangeEnd(null);
      setNotice(null); setError(null);
    }
  }

  function onRowPointerMove(e: ReactPointerEvent<HTMLDivElement>, days: ReadonlyArray<WeekDay>) {
    const drag = dayDragRef.current;
    if (!drag) return;
    const d = dayFromRow(e, days);
    if (!d || d.unavailable || dayKey(d.date) === dayKey(drag.anchor)) return;
    drag.moved = true;
    commitRange(drag.anchor, d.date);
  }

  function endRowDrag(e: ReactPointerEvent<HTMLDivElement>) {
    if (!dayDragRef.current) return;
    dayDragRef.current = null;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }

  /** 선택한 기간을 그 주(또는 월 행) 안에서 막대 좌표로. 걸치지 않으면 null. */
  function selectionBar(days: ReadonlyArray<WeekDay>): { startCol: number; span: number } | null {
    if (!rangeStart || days.length === 0) return null;
    const last = rangeEnd ?? rangeStart;
    const from = startOfDay(rangeStart).getTime();
    const to = startOfDay(last).getTime();
    const first = startOfDay(days[0]!.date).getTime();
    const dayMs = 24 * 60 * 60 * 1000;
    const a = Math.max(0, Math.round((from - first) / dayMs));
    const b = Math.min(days.length - 1, Math.round((to - first) / dayMs));
    if (b < 0 || a > days.length - 1 || b < a) return null;
    return { startCol: a + 1, span: b - a + 1 };
  }

  /** 일 단위 막대 한 개 — 주/월 공통. */
  function renderSpanBar(b: SpanBar, row: number) {
    return (
      <div
        key={b.id}
        className={[
          'rsv-sp',
          b.mine ? 'is-mine' : '',
          b.status === 'pending' ? 'is-pend' : '',
          b.continuesLeft ? 'is-contl' : '',
          b.continuesRight ? 'is-contr' : '',
        ].filter(Boolean).join(' ')}
        style={{ gridColumn: `${b.startCol} / span ${b.span}`, gridRow: row }}
        title={`${fmtRange(b.start, b.end, 'day')} · ${b.requesterName}`}
      >
        <b>{b.mine ? tRsv('rsv.week.mine') : (b.requesterName || tRsv('rsv.title'))}</b>
        <span>
          {' · '}{fmtDayShort(b.start, b.end)}
          {b.purpose ? ` · ${b.purpose}` : ''}
          {b.status === 'pending' ? ` · ${tRsv('rsv.status.pending')}` : ''}
        </span>
      </div>
    );
  }

  // ── 이동 ─────────────────────────────────────────────────────────
  function move(delta: number) {
    const next = new Date(cursor);
    if (effectiveView === 'week') {
      next.setDate(next.getDate() + delta * 7);
    } else {
      next.setDate(1);
      next.setMonth(next.getMonth() + delta);
    }
    setCursor(next);
  }

  const rangeLabel = useMemo(() => {
    if (effectiveView === 'month') {
      return `${cursor.getFullYear()}.${String(cursor.getMonth() + 1).padStart(2, '0')}`;
    }
    const first = weekDays[0]?.date ?? cursor;
    const last = weekDays[6]?.date ?? cursor;
    const p = (n: number) => String(n).padStart(2, '0');
    return `${first.getFullYear()}.${p(first.getMonth() + 1)}.${p(first.getDate())} – ${p(last.getMonth() + 1)}.${p(last.getDate())}`;
  }, [effectiveView, weekDays, cursor]);

  function pickQuick(r: ReservationResourceDto) {
    const status = live[r.id];
    const startMin = snapToSlot(now.getHours() * 60 + now.getMinutes(), r, 'ceil');
    let endMin = Math.min(r.closeMinute, startMin + r.slotMinutes);
    // 연속으로 비는 끝을 넘기지 않는다 — 넘기면 서버가 CAPACITY 로 거절한다.
    if (status?.freeUntil) {
      const untilMin = status.freeUntil.getHours() * 60 + status.freeUntil.getMinutes();
      endMin = Math.min(endMin, untilMin === 0 ? 24 * 60 : untilMin);
    }
    if (endMin <= startMin) return;
    const start = startOfDay(now); start.setMinutes(startMin);
    const end = startOfDay(now); end.setMinutes(endMin);
    carryRef.current = { start, end };
    setView('week');
    setCursor(new Date());
    if (r.id === selectedId) {
      carryRef.current = null;
      setWeekSel({ start, end });
    } else {
      setSelectedId(r.id);
    }
  }

  async function submit() {
    if (!selected || !pending) return;
    setSubmitting(true); setError(null); setNotice(null);
    try {
      const created = await createReservation({
        resourceId: selected.id,
        startAt: pending.start,
        endAt: pending.end,
        quantity,
        purpose: purpose.trim() === '' ? null : purpose.trim(),
      });
      setNotice(created.status === 'approved' ? tRsv('rsv.book.ok_approved') : tRsv('rsv.book.ok_pending'));
      clearSelection();
      setPurpose('');
      await reloadSlots(selected.id, effectiveView, cursor);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? tRsv('rsv.book.err'));
    } finally {
      setSubmitting(false);
    }
  }

  function liveText(r: ReservationResourceDto): { kind: string; text: string } {
    const s = live[r.id];
    if (!s) return { kind: 'unknown', text: tRsv('rsv.live.unknown') };
    if (s.kind === 'free') {
      return {
        kind: 'free',
        text: s.freeUntil
          ? tRsv('rsv.live.free_until', { time: fmtTime(s.freeUntil) })
          : tRsv('rsv.live.free'),
      };
    }
    if (s.kind === 'busy') {
      return {
        kind: 'busy',
        text: s.freeAt ? tRsv('rsv.live.busy_until', { time: fmtTime(s.freeAt) }) : tRsv('rsv.live.busy'),
      };
    }
    return {
      kind: 'out',
      text: s.freeAt ? tRsv('rsv.live.opens_at', { time: fmtTime(s.freeAt) }) : tRsv('rsv.live.out'),
    };
  }

  return (
    <div className="rsv-screen">
      {/* ② 좌측 상세 — 검색·필터·자원 목록 */}
      <ReservationAside>
        <div className="rsv-aside__body">
          <input
            className="rsv-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={tRsv('rsv.resources.search')}
            aria-label={tRsv('rsv.resources.search')}
          />
          <div className="rsv-fchips">
            <button
              type="button"
              className={`rsv-fchip${filter === 'all' ? ' is-on' : ''}`}
              onClick={() => setFilter('all')}
            >
              {tRsv('rsv.filter.all')}
            </button>
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                className={`rsv-fchip${filter === `cat:${c}` ? ' is-on' : ''}`}
                onClick={() => setFilter(`cat:${c}`)}
              >
                {c}
              </button>
            ))}
            <button
              type="button"
              className={`rsv-fchip${filter === 'now' ? ' is-on' : ''}`}
              onClick={() => setFilter('now')}
            >
              {tRsv('rsv.filter.now')}
            </button>
            <button
              type="button"
              className={`rsv-fchip${filter === 'instant' ? ' is-on' : ''}`}
              onClick={() => setFilter('instant')}
            >
              {tRsv('rsv.filter.instant')}
            </button>
          </div>

          {listErr && <p className="rsv-error" role="alert">{listErr}</p>}
          {!listErr && resources.length === 0 && <p className="rsv-empty">{tRsv('rsv.resources.empty')}</p>}
          {!listErr && resources.length > 0 && filtered.length === 0 && (
            <p className="rsv-empty">{tRsv('rsv.resources.none_match')}</p>
          )}

          {groups.map((g) => (
            <div key={g.key}>
              <div className="rsv-grouphead">{g.label}</div>
              {g.rows.map((r) => {
                const l = liveText(r);
                const s = live[r.id];
                const isFav = favorites.includes(r.id);
                return (
                  <div key={r.id} className={`rsv-res${r.id === selectedId ? ' is-on' : ''}`}>
                    <button
                      type="button"
                      className="rsv-res__pick"
                      onClick={() => setSelectedId(r.id)}
                      aria-pressed={r.id === selectedId}
                    >
                      <span className="rsv-res__n">{r.name}</span>
                      <span className="rsv-res__sub">
                        {[
                          r.location,
                          tRsv('rsv.resource.capacity', { n: r.capacity }),
                          r.requiresApproval ? tRsv('rsv.resource.approval') : tRsv('rsv.resource.instant'),
                        ].filter(Boolean).join(' · ')}
                      </span>
                      <span className={`rsv-live rsv-live--${l.kind}`}>
                        <span className="rsv-dot" aria-hidden />
                        {l.text}
                        {s && s.kind === 'free' && s.freeMinutes > 0 && (
                          <span className="rsv-live__more">
                            {tRsv('rsv.live.today_free', { dur: tRsvDuration(s.freeMinutes) })}
                          </span>
                        )}
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`rsv-star${isFav ? ' is-on' : ''}`}
                      onClick={() => toggleFavorite(r.id)}
                      aria-label={isFav ? tRsv('rsv.fav.remove') : tRsv('rsv.fav.add')}
                      aria-pressed={isFav}
                    >
                      <Star size={12} fill={isFav ? 'currentColor' : 'none'} aria-hidden />
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </ReservationAside>

      {/* ③ 컨텐츠 */}
      <section className="rsv-content">
        {/*
          ★★★ 화면 머리 — **정본 `PageHeader`(A3)** (2026-09-08 사용자 지적).
             예약은 레일 제목(`SecondaryPane`)만 있고 **화면 머리가 통째로 없었다** —
             경로 · 제목 · 한 줄 설명 · 도움말이 다 빠져 있었다.
          ★ 아래 `.rsv-chead` 는 **고른 자원의 머리**라 다른 물건이다 — 그대로 둔다.
        */}
        <PageHeader
          className="rsv-head"
          breadcrumb={[tRsv('rsv.title'), tRsv('rsv.nav.book')]}
          title={tRsv('rsv.nav.book')}
          description={tRsv('rsv.head.desc_book')}
          help={{
            title: tRsv('rsv.head.help_title'),
            body: (
              <>
              <p><b>자원을 먼저 고릅니다</b> — 왼쪽 목록에서 고르면 그 자원의 빈 시간이 보입니다.</p>
              <p>드래그로 시간을 잡습니다. 승인이 필요한 자원은 <b>신청</b>으로 올라가고, 아니면 바로 잡힙니다.</p>
              <p>남이 잡아 둔 칸은 고를 수 없습니다 — 겹치는 예약은 서버가 막습니다.</p>
              </>
            ),
          }}
        />
        {!selected ? (
          <p className="rsv-empty">{tRsv('rsv.resource.select_hint')}</p>
        ) : (
          <>
            <header className="rsv-chead">
              <div className="rsv-chead__t">
                <strong>{selected.name}</strong>
                <span>
                  {[
                    selected.location,
                    selected.equipmentInfo,
                    tRsv('rsv.resource.capacity', { n: selected.capacity }),
                    selected.bookingUnit === 'day'
                      ? tRsv('rsv.resource.unit_day')
                      : tRsv('rsv.doc.slot_unit', { n: selected.slotMinutes }),
                    selected.requiresApproval ? tRsv('rsv.resource.approval') : tRsv('rsv.resource.instant'),
                  ].filter(Boolean).join(' · ')}
                </span>
              </div>
              <div className="rsv-chead__sp" />
              {/* 주·월 토글은 두 단위 모두에 있다 — 일 단위도 "이번 주 언제 비나" 를 묻는다. */}
              <div className="rsv-seg" role="group" aria-label={tRsv('rsv.week.grid')}>
                <button
                  type="button"
                  className={effectiveView === 'week' ? 'is-on' : ''}
                  onClick={() => setView('week')}
                >
                  {tRsv('rsv.view.week')}
                </button>
                <button
                  type="button"
                  className={effectiveView === 'month' ? 'is-on' : ''}
                  onClick={() => setView('month')}
                >
                  {tRsv('rsv.view.month')}
                </button>
              </div>
              <button type="button" className="rsv-iconbtn" onClick={() => move(-1)} aria-label={tRsv('rsv.cal.prev')}>
                <ChevronLeft size={14} aria-hidden />
              </button>
              <span className="rsv-datelabel">{rangeLabel}</span>
              <button type="button" className="rsv-iconbtn" onClick={() => move(1)} aria-label={tRsv('rsv.cal.next')}>
                <ChevronRight size={14} aria-hidden />
              </button>
              <button type="button" className="rsv-btn rsv-btn--sm" onClick={() => setCursor(new Date())}>
                {tRsv('rsv.cal.today')}
              </button>
            </header>

            {/* 일 단위엔 "지금 바로" 가 의미 없다 — 대여는 시각이 아니라 날짜다. */}
            {selected.bookingUnit === 'day' ? (
              <div className="rsv-quick">
                <span className="rsv-quick__l">{tRsv('rsv.week.pick_range')}</span>
                <div className="rsv-chead__sp" />
                <span className="rsv-quick__l">
                  {tRsv('rsv.week.range_note', { n: selected.capacity })}
                </span>
              </div>
            ) : effectiveView === 'week' ? (
              <div className="rsv-quick">
                <span className="rsv-quick__l">{tRsv('rsv.week.now_label')}</span>
                {quickFree.length === 0 ? (
                  <span className="rsv-quick__l">{tRsv('rsv.week.now_empty')}</span>
                ) : quickFree.map((r) => {
                  const until = live[r.id]?.freeUntil ?? null;
                  return (
                    <button key={r.id} type="button" className="rsv-qchip" onClick={() => pickQuick(r)}>
                      <span className="rsv-dot" aria-hidden />
                      {r.name}
                      {until && <b>{tRsv('rsv.week.until_short', { time: fmtTime(until) })}</b>}
                    </button>
                  );
                })}
                <div className="rsv-chead__sp" />
                <span className="rsv-quick__l">{tRsv('rsv.week.drag_hint')}</span>
              </div>
            ) : (
              <div className="rsv-quick">
                <span className="rsv-quick__l">
                  {tRsv('rsv.month.summary', { n: reservations.length })}
                </span>
                <div className="rsv-chead__sp" />
                <span className="rsv-quick__l">{tRsv('rsv.month.tap_day')}</span>
              </div>
            )}

            {notice && <p className="rsv-notice" role="status"><Check size={14} aria-hidden /> {notice}</p>}
            {error && <p className="rsv-error" role="alert">{error}</p>}

            {effectiveView === 'week' && selected.bookingUnit === 'day' ? (
              /* 일 단위 주간 — 시간 격자 대신 날짜에 걸친 기간 막대(레인). */
              (() => {
                const bars = layoutSpanBars(reservations, weekDays);
                const selBar = selectionBar(weekDays);
                const laneRows = Math.max(3, bars.length > 0 ? bars[0]!.lanes : 1) + (selBar ? 1 : 0);
                return (
                  <div className="rsv-dayweek scrollbar-thin">
                    <div
                      className="rsv-dwgrid"
                      role="grid"
                      aria-label={tRsv('rsv.week.lanes')}
                      aria-busy={loadingSlots || undefined}
                      onPointerDown={(e) => onRowPointerDown(e, weekDays)}
                      onPointerMove={(e) => onRowPointerMove(e, weekDays)}
                      onPointerUp={endRowDrag}
                      onPointerCancel={endRowDrag}
                    >
                      {weekDays.map((d, i) => {
                        const left = dayRemainingCount(selected, d.date, reservations);
                        return (
                          <div
                            key={`h-${dayKey(d.date)}`}
                            className={[
                              'rsv-dwhead',
                              d.isToday ? 'is-today' : '',
                              d.isWeekend ? 'is-weekend' : '',
                            ].filter(Boolean).join(' ')}
                            style={{ gridColumn: i + 1, gridRow: 1 }}
                          >
                            <span className="rsv-dwhead__d">
                              {WEEK_DOW[i]}{d.isToday ? ` · ${tRsv('rsv.cal.today')}` : ''}
                            </span>
                            <span className="rsv-dwhead__n">{d.date.getDate()}</span>
                            <span className={`rsv-dwhead__left${d.unavailable || left === 0 ? ' is-none' : ''}`}>
                              {d.unavailable
                                ? tRsv('rsv.week.unavailable')
                                : tRsv('rsv.live.left_n', { n: left })}
                            </span>
                          </div>
                        );
                      })}

                      {weekDays.map((d, i) => (
                        <div
                          key={`c-${dayKey(d.date)}`}
                          className={[
                            'rsv-dwcell',
                            d.isToday ? 'is-today' : '',
                            d.unavailable ? 'is-off' : '',
                          ].filter(Boolean).join(' ')}
                          style={{ gridColumn: i + 1, gridRow: `2 / span ${laneRows}` }}
                        />
                      ))}

                      {bars.map((b) => renderSpanBar(b, 2 + b.lane))}

                      {selBar && (
                        <div
                          className="rsv-sp is-sel"
                          style={{
                            gridColumn: `${selBar.startCol} / span ${selBar.span}`,
                            gridRow: 2 + Math.max(0, laneRows - 1),
                          }}
                        >
                          {pending ? fmtDayShort(pending.start, pending.end) : ''}
                          {pendingMinutes > 0 ? ` · ${tRsvDuration(pendingMinutes)}` : ''}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()
            ) : effectiveView === 'week' ? (
              <div className="rsv-weekwrap scrollbar-thin">
                <div
                  className="rsv-weekgrid"
                  role="grid"
                  aria-label={tRsv('rsv.week.grid')}
                  aria-busy={loadingSlots || undefined}
                >
                  <div className="rsv-dayhead rsv-dayhead--corner" />
                  {weekColumns.map(({ day, freeMinutes }, i) => (
                    <div
                      key={dayKey(day.date)}
                      className={[
                        'rsv-dayhead',
                        day.isToday ? 'is-today' : '',
                        day.isWeekend ? 'is-weekend' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <div className="rsv-dayhead__d">
                        {WEEK_DOW[i]}{day.isToday ? ` · ${tRsv('rsv.cal.today')}` : ''}
                      </div>
                      <div className="rsv-dayhead__n">{day.date.getDate()}</div>
                      <div className={`rsv-dayhead__free${freeMinutes > 0 ? '' : ' is-none'}`}>
                        {day.unavailable
                          ? tRsv('rsv.week.unavailable')
                          : freeMinutes > 0
                            ? tRsv('rsv.week.free', { dur: tRsvDuration(freeMinutes) })
                            : tRsv('rsv.week.free_none')}
                      </div>
                    </div>
                  ))}

                  <div className="rsv-hours">
                    {win.hours.map((h) => (
                      <div key={h} className="rsv-hour">{String(h).padStart(2, '0')}</div>
                    ))}
                  </div>

                  {weekColumns.map(({ day, events }) => {
                    const isSelDay = weekSel && dayKey(weekSel.start) === dayKey(day.date);
                    const selTop = weekSel
                      ? minuteToY(weekSel.start.getHours() * 60 + weekSel.start.getMinutes(), win)
                      : 0;
                    const selH = weekSel
                      ? ((weekSel.end.getTime() - weekSel.start.getTime()) / 3600000) * HOUR_PX
                      : 0;
                    const nowY = minuteToY(now.getHours() * 60 + now.getMinutes(), win);
                    return (
                      <div
                        key={dayKey(day.date)}
                        className={[
                          'rsv-col',
                          day.isToday ? 'is-today' : '',
                          day.unavailable ? 'is-off' : '',
                        ].filter(Boolean).join(' ')}
                        style={{ height: `${win.heightPx}px` }}
                        onPointerDown={(e) => onColumnPointerDown(e, day)}
                        onPointerMove={(e) => onColumnPointerMove(e, day)}
                        onPointerUp={endDrag}
                        onPointerCancel={endDrag}
                        role="gridcell"
                        aria-label={tRsv('rsv.week.day_events', { date: fmtDate(day.date), n: events.length })}
                      >
                        {events.map((ev) => (
                          <div
                            key={ev.id}
                            className={[
                              'rsv-ev',
                              ev.mine ? 'rsv-ev--mine' : '',
                              ev.status === 'pending' ? 'rsv-ev--pend' : '',
                              ev.past ? 'rsv-ev--past' : '',
                            ].filter(Boolean).join(' ')}
                            style={{
                              top: `${ev.topPx}px`,
                              height: `${ev.heightPx}px`,
                              left: `calc(${(ev.lane / ev.lanes) * 100}% + 3px)`,
                              width: `calc(${100 / ev.lanes}% - 6px)`,
                            }}
                            title={`${fmtTime(ev.start)}–${fmtTime(ev.end)} · ${ev.requesterName}`}
                          >
                            {/* 목적은 내 건에만 온다(타인 건은 서버가 가린다) — 없으면 이름이 제목이 된다.
                                제목과 아래 줄에 같은 이름을 두 번 찍지 않는다. */}
                            <b>
                              {ev.mine ? `${tRsv('rsv.week.mine')} · ` : ''}
                              {ev.purpose || ev.requesterName || tRsv('rsv.title')}
                            </b>
                            <span>
                              {fmtTime(ev.start)}–{fmtTime(ev.end)}
                              {ev.purpose && ev.requesterName ? ` · ${ev.requesterName}` : ''}
                            </span>
                          </div>
                        ))}

                        {isSelDay && weekSel && (
                          <div className="rsv-sel" style={{ top: `${selTop}px`, height: `${selH}px` }}>
                            <span
                              className="rsv-sel__grip is-top"
                              onPointerDown={(e) => onGripPointerDown(e, day, 'start')}
                            />
                            {fmtTime(weekSel.start)} – {fmtTime(weekSel.end)}
                            <small>{tRsvDuration(pendingMinutes)}</small>
                            <span
                              className="rsv-sel__grip is-bottom"
                              onPointerDown={(e) => onGripPointerDown(e, day, 'end')}
                            />
                          </div>
                        )}

                        {day.isToday && nowY >= 0 && nowY <= win.heightPx && (
                          <div className="rsv-now" style={{ top: `${nowY}px` }} aria-hidden />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* 월 뷰 — 두 단위 공통. 시간 단위는 가용 막대 + 예약 칩, 일 단위는 기간 막대. */
              <div className="rsv-monthwrap scrollbar-thin">
                <div className="rsv-mgrid" role="grid" aria-label={tRsv('rsv.month.grid')} aria-busy={loadingSlots || undefined}>
                  {WEEK_DOW.map((d, i) => (
                    <div key={d} className={`rsv-mdow${i >= 5 ? ' is-weekend' : ''}`}>{d}</div>
                  ))}

                  {monthWeeks.map((week, wi) => {
                    const bars = selected.bookingUnit === 'day' ? layoutSpanBars(reservations, week) : [];
                    const selBar = selected.bookingUnit === 'day' ? selectionBar(week) : null;
                    return (
                      <Fragment key={`w-${wi}`}>
                        <div
                          className="rsv-mrow"
                          onPointerDown={(e) => onRowPointerDown(e, week)}
                          onPointerMove={(e) => onRowPointerMove(e, week)}
                          onPointerUp={endRowDrag}
                          onPointerCancel={endRowDrag}
                        >
                          {week.map((d) => {
                            const isThis = inMonth(d.date, cursor);
                            const isDay = selected.bookingUnit === 'day';
                            const left = isDay ? dayRemainingCount(selected, d.date, reservations) : 0;
                            const ratio = isDay ? 0 : dayFreeRatio(selected, d.date, reservations, now);
                            const chips = isDay
                              ? []
                              : layoutDayEvents(reservations, d.date, win, now).slice(0, 2);
                            const chipTotal = isDay
                              ? 0
                              : reservationsOfDay(reservations, d.date).length;
                            const pct = isDay ? (left / Math.max(1, selected.capacity)) * 100 : ratio * 100;
                            const tone = pct <= 0 ? ' is-zero' : pct < 45 ? ' is-low' : '';
                            const inRange = isDay && pending
                              && d.date.getTime() >= pending.start.getTime()
                              && d.date.getTime() < pending.end.getTime();
                            return (
                              <div
                                key={dayKey(d.date)}
                                role="gridcell"
                                tabIndex={d.unavailable ? -1 : 0}
                                className={[
                                  'rsv-mcell',
                                  isThis ? '' : 'is-out',
                                  d.isToday ? 'is-today' : '',
                                  d.unavailable ? 'is-off' : '',
                                  inRange ? 'is-range' : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => {
                                  // 시간 단위는 월에서 훑고 주에서 고른다 — 셀이 30분 격자를 담기엔 작다.
                                  if (d.unavailable || selected.bookingUnit === 'day') return;
                                  setCursor(d.date);
                                  setView('week');
                                }}
                                onKeyDown={(e) => {
                                  if (d.unavailable || selected.bookingUnit === 'day') return;
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setCursor(d.date);
                                    setView('week');
                                  }
                                }}
                              >
                                <div className="rsv-mcell__top">
                                  <span className="rsv-mcell__d">{d.date.getDate()}</span>
                                  {!d.unavailable && (
                                    <span className={`rsv-mcell__left${pct <= 0 ? ' is-none' : ''}`}>
                                      {isDay
                                        ? tRsv('rsv.week.left_of', { n: left, total: selected.capacity })
                                        : (pct <= 0
                                          ? tRsv('rsv.cal.full')
                                          : tRsv('rsv.week.free', {
                                            dur: tRsvDuration(freeMinutesOfDay(selected, d.date, reservations, now)),
                                          }))}
                                    </span>
                                  )}
                                </div>
                                {!d.unavailable && (
                                  <div className={`rsv-mbar${tone}`}><i style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} /></div>
                                )}
                                {chips.map((ev) => (
                                  <div
                                    key={ev.id}
                                    className={[
                                      'rsv-mchip',
                                      ev.mine ? 'is-mine' : '',
                                      ev.status === 'pending' ? 'is-pend' : '',
                                    ].filter(Boolean).join(' ')}
                                    title={`${fmtTime(ev.start)}–${fmtTime(ev.end)} · ${ev.requesterName}`}
                                  >
                                    {fmtTime(ev.start)} {ev.purpose || ev.requesterName || tRsv('rsv.title')}
                                  </div>
                                ))}
                                {chipTotal > chips.length && (
                                  <div className="rsv-mmore">{tRsv('rsv.month.more', { n: chipTotal - chips.length })}</div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {(bars.length > 0 || selBar) && (
                          <div className="rsv-mlanes">
                            {bars.map((b) => renderSpanBar(b, 1 + b.lane))}
                            {selBar && (
                              <div
                                className="rsv-sp is-sel"
                                style={{
                                  gridColumn: `${selBar.startCol} / span ${selBar.span}`,
                                  gridRow: 1 + (bars.length > 0 ? bars[0]!.lanes : 0),
                                }}
                              >
                                {pending ? fmtDayShort(pending.start, pending.end) : ''}
                              </div>
                            )}
                          </div>
                        )}
                      </Fragment>
                    );
                  })}
                </div>

                {loadingSlots && <p className="rsv-empty">{tRsv('rsv.cal.loading')}</p>}
              </div>
            )}
          </>
        )}
      </section>

      {/* ④ 신청 패널 — **고른 뒤에만** 나온다. 자원만 골랐을 때 빈 패널이 300px 를 먹던 것을 고친다. */}
      {selected && pending && (
        <aside className="rsv-doc" aria-label={tRsv('rsv.doc.title')}>
          <div className="rsv-doc__h">
            <Clock size={15} aria-hidden />
            {tRsv('rsv.doc.title')}
          </div>

          <div className="rsv-doc__b scrollbar-thin">
            <div className="rsv-field">
              <span className="rsv-field__l">{tRsv('rsv.doc.target')}</span>
              <span className="rsv-field__v">
                {[selected.name, selected.location].filter(Boolean).join(' · ')}
              </span>
              {selected.owner && (
                <span className="rsv-field__owner">
                  <Avatar
                    userId={Number(selected.owner.id ?? 0)}
                    name={selected.owner.name}
                    src={selected.owner.avatarUrl}
                    size="xs"
                  />
                  <span>{selected.owner.name}</span>
                  {selected.owner.position && <em>{selected.owner.position}</em>}
                </span>
              )}
            </div>

            <div className="rsv-field">
              <span className="rsv-field__l">{tRsv('rsv.doc.when')}</span>
              {pending ? (
                <>
                  <span className="rsv-field__v rsv-field__v--big">
                    {fmtRange(pending.start, pending.end, selected.bookingUnit)}
                  </span>
                  <span className="rsv-field__hint">
                    {[
                      tRsvDuration(pendingMinutes),
                      selected.bookingUnit === 'time'
                        ? tRsv('rsv.doc.slot_unit', { n: selected.slotMinutes })
                        : '',
                    ].filter(Boolean).join(' · ')}
                  </span>
                </>
              ) : (
                <span className="rsv-field__hint">{tRsv('rsv.doc.empty')}</span>
              )}
            </div>

            <div className={`rsv-note${selected.requiresApproval ? '' : ' rsv-note--ok'}`}>
              {selected.requiresApproval
                ? <AlertTriangle size={13} aria-hidden />
                : <Check size={13} aria-hidden />}
              <div>{selected.requiresApproval ? tRsv('rsv.doc.approval_note') : tRsv('rsv.doc.instant_note')}</div>
            </div>

            {pending && pendingRemaining === 0 && (
              <div className="rsv-note rsv-note--bad">
                <AlertTriangle size={13} aria-hidden />
                <div>{tRsv('rsv.cal.full')}</div>
              </div>
            )}

            <div className="rsv-field">
              <span className="rsv-field__l">{tRsv('rsv.book.purpose')}</span>
              <input
                className="rsv-input"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder={tRsv('rsv.book.purpose_ph')}
                aria-label={tRsv('rsv.book.purpose')}
                maxLength={300}
              />
            </div>

            {selected.capacity > 1 && (
              <div className="rsv-field">
                <span className="rsv-field__l">
                  {tRsv('rsv.book.quantity')} · {tRsv('rsv.doc.qty_max', { n: selected.capacity })}
                </span>
                <div className="rsv-stepper">
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    aria-label="-"
                  >
                    −
                  </button>
                  <span>{quantity}</span>
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.min(selected.capacity, q + 1))}
                    aria-label="+"
                  >
                    +
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="rsv-doc__f">
            <button
              type="button"
              className="rsv-btn rsv-btn--primary rsv-btn--block"
              disabled={submitting || !pending || pendingRemaining === 0}
              onClick={() => void submit()}
            >
              {submitting ? tRsv('rsv.book.submitting') : tRsv('rsv.book.submit')}
            </button>
            {pending && (
              <button type="button" className="rsv-btn rsv-btn--block" onClick={clearSelection}>
                <X size={12} aria-hidden /> {tRsv('rsv.doc.clear')}
              </button>
            )}
            <div className="rsv-mini">{tRsv('rsv.doc.submit_hint')}</div>
          </div>
        </aside>
      )}
    </div>
  );
}
